import { Alert, Box, Paper, Snackbar, Tab, Tabs, Typography } from "@mui/material"
import { CheckCircleFilledRounded, WarningOutlined } from "@mui-symbols-material/w300"
import type { AnyRoute } from "@tanstack/react-router"
import { Link, Outlet, createFileRoute, useLocation, useParams } from "@tanstack/react-router"
import React, { useEffect, useState } from "react"

import { type Company, type CompanyGetSuccess, CompanyStatus } from "@/graphql/codegen/graphql"
import { useGetLocations } from "@/hooks/useGetLocations"
import { COMPANY_EDIT_ROUTE } from "@/screens/Companies/constants"
import CompanyComments from "@/screens/Companies/edit/components/CompanyComments"
import EditCompanyModal from "@/screens/Companies/edit/components/locations/EditCompanyModal"
import { useGetCompanies } from "@/screens/Companies/hooks/useGetCompanies"
import { useCompanyStore } from "@/screens/Companies/stores/useCompanyStore"
import { useCompanyTypesStore } from "@/screens/Companies/stores/useCompanyTypesStore"
import CompanyHeader from "@/screens/Companies/view/components/CompanyHeader"
import { deepEqualIgnoreTypename } from "@/utils/deepEqualIgnoreTypename"

const createTabList = (companyId: string) => [
  { label: "Locations", path: `/app/companies/${companyId}/edit/locations` },
  { label: "Contacts", path: `/app/companies/${companyId}/edit/contacts` },
  { label: "Products", path: `/app/companies/${companyId}/edit/products` },
  { label: "Accounting", path: `/app/companies/${companyId}/edit/accounting` },
  { label: "Evaluation", path: `/app/companies/${companyId}/edit/evaluation` },
  { label: "Info", path: `/app/companies/${companyId}/edit/info` },
  { label: "Registration", path: `/app/companies/${companyId}/edit/registration` },
  {
    label: "Certification",
    path: `/app/companies/${companyId}/edit/certification`,
  },
  { label: "Preferences", path: `/app/companies/${companyId}/edit/preferences` },
]

const CompanyEditLayout: React.FC = () => {
  const location = useLocation()
  const { companyId: queryCompanyId } = useParams({
    from: COMPANY_EDIT_ROUTE,
  })
  const tabs = createTabList(queryCompanyId)

  // Determine the current tab based on the pathname
  const currentTab = tabs.find((tab) => location.pathname.startsWith(tab.path)) || tabs[0]

  const [open, setOpen] = useState(false)
  const [showSuccessNotification, setShowSuccessNotification] = useState<boolean>(false)
  const [showWarningNotification, setShowWarningNotification] = useState<boolean>(false)
  const { companyDetails, companyDetailsFetching, reexecuteCompanyDetailsQuery } = useGetCompanies(queryCompanyId)
  const { locations, getLocationsWithReset } = useGetLocations(queryCompanyId)
  const { companyId, name, description, status, preferredMeasurementSystem, defaultUom, types } =
    (companyDetails?.company.get as CompanyGetSuccess)?.company || {}

  // Create properly typed variables with fallbacks
  const companyName = name ?? ""
  const companyTypesList = types ?? []
  const company = useCompanyStore((state) => state.company)
  const setCompany = useCompanyStore((state) => state.setCompany)

  const fetchCompanyTypes = useCompanyTypesStore((state) => state.fetchCompanyTypes)
  const companyTypeFetching = useCompanyTypesStore((state) => state.fetching)
  const companyTypes = useCompanyTypesStore((state) => state.companyTypes)

  useEffect(() => {
    if (companyDetails?.company.get.__typename === "CompanyGetSuccess") {
      setCompany(companyDetails?.company.get.company)
    }
  }, [companyDetails, setCompany])

  useEffect(() => {
    if (!companyTypeFetching && companyTypes.length === 0) {
      fetchCompanyTypes()
    }
  }, [companyTypeFetching, companyTypes, fetchCompanyTypes])

  const handleCompanyEditModalClose = () => setOpen(false)

  const handleCompanyEditModalSuccess = (updatedCompany: Company) => {
    const hasChanges = !deepEqualIgnoreTypename(updatedCompany, {
      name,
      types,
    })

    if (!hasChanges) {
      setOpen(false)
      setShowWarningNotification(true)
      return
    }
    setShowSuccessNotification(true)
    getLocationsWithReset()
    reexecuteCompanyDetailsQuery()
  }

  const closeSnackbar = () => {
    setShowSuccessNotification(false)
    setShowWarningNotification(false)
  }

  const showCompanyComments = company?.status === CompanyStatus.Inactive || company?.status === CompanyStatus.Pending

  return companyDetailsFetching ? (
    <Typography variant='h5'>Loading...</Typography>
  ) : (
    <div className='flex h-full flex-col gap-4'>
      <Paper elevation={0} className='flex min-h-0 grow flex-col space-y-7 overflow-hidden bg-white'>
        <Box className='flex items-center justify-between'>
          <Typography variant='h5' color='primary' fontWeight='normal'>
            {company?.status === CompanyStatus.Pending ? "Create New Company" : "Company Details"}
          </Typography>
        </Box>
        <Box className='space-y-2.5'>
          {company && (
            <CompanyHeader
              company={company}
              name={company?.name ?? companyName}
              status={company?.status ?? status ?? CompanyStatus.Pending}
              types={company?.types ?? companyTypesList}
              onCompanyRefresh={reexecuteCompanyDetailsQuery}
              onEditClick={setOpen}
              locationsCount={locations?.length ?? 0}
            />
          )}
          {showCompanyComments && <CompanyComments companyId={companyId} />}
        </Box>
        <Box className='flex w-full flex-1 flex-col overflow-y-auto'>
          <Tabs value={currentTab.path} variant='scrollable' scrollButtons='auto'>
            {tabs.map((tab, index) => (
              <Tab key={index} label={tab.label} value={tab.path} component={Link} to={tab.path} />
            ))}
          </Tabs>
          <Box className='flex min-h-0 grow flex-col py-4'>
            <Outlet />
          </Box>
        </Box>
        <EditCompanyModal
          isOpen={open}
          data={{
            name: companyName,
            description,
            types: companyTypesList,
            status: company?.status ?? status ?? CompanyStatus.Pending,
            preferredMeasurementSystem,
            defaultUom,
          }}
          companyId={companyId}
          onClose={handleCompanyEditModalClose}
          onSuccess={handleCompanyEditModalSuccess}
        />
        <Snackbar
          open={showWarningNotification || showSuccessNotification}
          autoHideDuration={3000}
          onClose={closeSnackbar}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <Alert
            onClose={closeSnackbar}
            severity={showWarningNotification ? "warning" : "success"}
            variant='filled'
            sx={{ width: "100%" }}
            classes={{
              root: showWarningNotification ? "bg-[#FFF4E5] text-[#663C00]" : "bg-success-100 text-[#1E4620]",
            }}
            icon={
              showWarningNotification ? (
                <WarningOutlined className='ml-2 text-[#EF6C00]' />
              ) : (
                <CheckCircleFilledRounded className='ml-2 text-success-500' />
              )
            }
          >
            <p>
              {showWarningNotification
                ? "No changes updated to your company profile"
                : "Company profile successfully updated."}
            </p>
          </Alert>
        </Snackbar>
      </Paper>
    </div>
  )
}

export const Route: AnyRoute = createFileRoute("/app/companies/$companyId/edit/_layout")({
  component: CompanyEditLayout,
})
